import React, { useState } from "react";
import { Modal, ModalBody, ModalFooter } from "reactstrap";

import {
  FeatureGrants,
  BooleanFeature,
  MergeFeatureGrants,
  NumericFeature,
  isBooleanFeature,
  isNumericFeature,
  SubscriptionSchema as Subscription,
  SubscriptionType,
  Tier,
  tierDefs,
} from "@pollyslack/hub/src/features/types";

import { DateTime } from "../Forms/DateTime";
import { PlanSelector } from "../Forms/PlanSelector";
import { getEnumValues } from "@pollyslack/hub/src/utils";
import moment from "moment";

// Light-red styling for an "included in plan" badge that's also sitting in
// featureOverrides as a redundant, already-granted override.
const redundantBadgeStyle: React.CSSProperties = {
  backgroundColor: "#f8d7da",
  borderColor: "#f5c2c7",
  color: "#842029",
};

type UpdateSubscriptionModalProps = {
  isOpen: boolean;
  subscription: Subscription;
  onClose: () => void;
  onSubmit: (params: {
    endDate?: string;
    tier: Tier;
    isTrial: boolean;
    stripeCustomerId?: string;
    stripeSubscriptionItemId?: string;
    billingAdminIds?: string[];
    subscribingUserId?: string;
    /** if true, you should wipe the endDate in mongo */
    clearEndDate: boolean;
    featureOverrides?: FeatureGrants;
    licenses?: number;
  }) => void;
};

export const UpdateSubscriptionModal: React.FunctionComponent<
  UpdateSubscriptionModalProps
> = (props: UpdateSubscriptionModalProps) => {
  const [endDate, setEndDate] = useState<Date | undefined>(
    props.subscription.endDate,
  );
  const [clearEndDate, setClearEndDate] = useState(false);
  const [tier, setTier] = useState(props.subscription.tier);
  const [isTrial, setIsTrial] = useState(props.subscription.isTrial);
  const [stripeCustomerId, setStripeCustomerId] = useState(
    props.subscription.payment?.stripeCustomerId,
  );
  const [stripeSubscriptionItemId, setStripeSubscriptionItemId] = useState(
    props.subscription.payment?.stripeSubscriptionItemId,
  );
  const [billingAdminIds, setBillingAdminIds] = useState(
    props.subscription.billingAdminIds,
  );

  const [featureOverrides, setFeatureOverrides] = useState<FeatureGrants>(
    props.subscription.featureOverrides ?? {},
  );
  // default licenses to 1 in case it's missing in the db
  const [licenses, setLicenses] = useState(props.subscription.licenses ?? 1);
  const [licenseValidationError, setLicenseValidationError] = useState("");
  // Bumped by "Clear all" to force the (uncontrolled) override checkboxes to
  // remount, since setFeatureOverrides alone won't uncheck rendered boxes.
  const [resetKey, setResetKey] = useState(0);

  const minLicenses = 0;
  function handleChangeLicenses(value: number) {
    if (value >= minLicenses) {
      setLicenseValidationError("");
    } else {
      setLicenseValidationError(`Minimum Number of Licenses is ${minLicenses}`);
    }
  }

  // Features the selected plan already grants — no need to let an operator
  // redundantly re-check one of these as an "override".
  const planFeatures = MergeFeatureGrants(...(tierDefs[tier]?.features ?? []));
  const booleanFeatures = getEnumValues(BooleanFeature);
  const overrideFeatures = [
    ...booleanFeatures.filter((f) => !planFeatures[f]),
    ...getEnumValues(NumericFeature),
  ].sort();
  const includedBooleanFeatures = booleanFeatures
    .filter((f) => planFeatures[f])
    .sort();

  function setBooleanFeatureOverride(f: BooleanFeature, v: boolean) {
    const updated = { ...featureOverrides };
    if (v) {
      updated[f] = true;
    } else {
      delete updated[f];
    }
    setFeatureOverrides(updated);
  }

  function setNumericFeatureOverride(f: NumericFeature, v: number) {
    const updated = { ...featureOverrides };
    updated[f] = v;
    setFeatureOverrides(updated);
  }

  function wipeEndDate() {
    setEndDate(undefined);
    setClearEndDate(true);
  }

  function setDate(date: Date) {
    setEndDate(date);
    setClearEndDate(false);
  }

  return (
    <Modal isOpen={props.isOpen} style={{ maxWidth: "700px" }}>
      <div className="modal-header">
        <h5 className="modal-title">Update Subscription</h5>
        <button
          type="button"
          className="close"
          data-dismiss="modal"
          aria-label="Close"
          onClick={props.onClose}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <ModalBody>
        <div className="form-row align-items-center">
          <div className="form-group col">
            <label className="font-weight-bold">💰 Select a Plan</label>
            <PlanSelector
              onChange={setTier}
              subscriptionType={props.subscription.subscriptionType}
              plan={props.subscription.tier || tier}
            />
          </div>
          {props.subscription.subscriptionType ===
            SubscriptionType.USER_LEVEL && (
            <div className="form-group col-3">
              <label>Number of Licenses</label>
              <input
                type="number"
                className={
                  licenseValidationError.length > 0
                    ? "form-control is-invalid"
                    : "form-control"
                }
                placeholder="License Count..."
                defaultValue={licenses}
                onChange={(e) => {
                  handleChangeLicenses(e.currentTarget.valueAsNumber);
                  setLicenses(e.currentTarget.valueAsNumber);
                }}
              />
              <span className="form-text text-danger">
                {licenseValidationError}
              </span>
            </div>
          )}
          <div className="form-group col-2">
            <label className="d-block">&nbsp;</label>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="is-trial"
                defaultChecked={isTrial}
                onChange={(e) => setIsTrial(e.currentTarget.checked)}
              />
              <label htmlFor="is-trial" className="form-check-label">
                Trial
              </label>
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group col-6">
            <label>Stripe Customer ID</label>
            <input
              type="text"
              className="form-control"
              placeholder="Starts with cus_"
              defaultValue={props.subscription.payment?.stripeCustomerId}
              onChange={(e) => setStripeCustomerId(e.currentTarget.value)}
            />
          </div>
          <div className="form-group col-6">
            <label>Stripe Subscription Item ID</label>
            <input
              type="text"
              className="form-control"
              pattern="si_.*"
              placeholder="Starts with si_"
              defaultValue={
                props.subscription.payment?.stripeSubscriptionItemId
              }
              onBlur={(e) => {
                if (
                  e.currentTarget.value &&
                  e.currentTarget.value.length > 0 &&
                  e.currentTarget.value.indexOf("si_") !== 0
                ) {
                  alert("Stripe Subscription Item ID should start with si_");
                  return;
                }
              }}
              onChange={(e) =>
                setStripeSubscriptionItemId(e.currentTarget.value)
              }
            />
          </div>
          <div className="form-group col-6">
            <label>
              Billing Admin Ids <i>(comma separated)</i>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Mongo userIds..."
              defaultValue={props.subscription.billingAdminIds}
              onChange={(e) =>
                setBillingAdminIds(e.currentTarget.value?.split(",") ?? [])
              }
            />
          </div>
          <div className="form-group col-6">
            <label>
              Expiration Date <i>(optional)</i>
            </label>
            <div className="d-flex">
              <div className="flex-grow-1 mr-1">
                <DateTime
                  onChange={(e) => setDate(moment(e).toDate())}
                  value={endDate}
                />
              </div>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={wipeEndDate}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        <hr className="my-3" />
        <div className="form-group">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="font-weight-bold mb-0">
              ⚙️ Select Feature Overrides{" "}
            </h6>
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => {
                setFeatureOverrides({});
                setResetKey((k) => k + 1);
              }}
            >
              Clear All
            </button>
          </div>
          {tier === Tier.FREE && Object.keys(featureOverrides).length > 0 && (
            <div className="alert alert-warning py-2 px-3 mb-3">
              ⚠️ {Object.keys(featureOverrides).length} feature override
              {Object.keys(featureOverrides).length === 1 ? "" : "s"} will
              remain active even on the FREE plan. Click 'Clear All' above to
              remove, else continue to keep these flags active.
            </div>
          )}
          <div className="overflow-auto px-5" style={{ maxHeight: "30vh" }}>
            {overrideFeatures.map((f) => (
              <div
                className="form-row"
                key={isBooleanFeature(f) ? `${f}-${resetKey}` : f}
              >
                <div className="col">
                  <label htmlFor={f}>{f}</label>
                </div>
                <div className="col-3 d-flex flex-row-reverse">
                  {isBooleanFeature(f) && (
                    <BooleanInput
                      id={f}
                      defaultValue={featureOverrides[f]}
                      onChange={(v) => setBooleanFeatureOverride(f, v)}
                    />
                  )}
                  {isNumericFeature(f) && (
                    <NumericInput
                      defaultValue={featureOverrides[f]}
                      minValue={0 /* TODO use selected plan allowance + 1 */}
                      onChange={(v) => setNumericFeatureOverride(f, v)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <hr className="my-3" />
        <div className="form-group">
          <h6 className="font-weight-bold mb-2">
            ✅ Features Included in Plan
          </h6>

          <div
            className="d-flex align-items-center mb-2 pl-2"
            style={{ fontSize: "0.85rem" }}
          >
            <span
              className="badge badge-light border font-weight-normal mr-2"
              style={{ fontSize: "0.95rem" }}
            >
              Feature1
            </span>
            <span className="text-muted mr-3">already granted by plan</span>
            <span
              className="badge border font-weight-normal mr-2"
              style={{ ...redundantBadgeStyle, fontSize: "0.95rem" }}
            >
              Feature2
            </span>
            <span className="text-muted">
              redundant override; click X to clean up
            </span>
          </div>
          <div className="d-flex flex-wrap pl-2">
            {includedBooleanFeatures.map((f) => {
              const hasRedundantOverride = featureOverrides[f] === true;
              return (
                <span
                  key={f}
                  className={
                    hasRedundantOverride
                      ? "badge border font-weight-normal mr-1 mb-1"
                      : "badge badge-light border font-weight-normal mr-1 mb-1"
                  }
                  style={{
                    fontSize: "0.95rem",
                    ...(hasRedundantOverride ? redundantBadgeStyle : {}),
                  }}
                >
                  {f}
                  {hasRedundantOverride && (
                    <button
                      type="button"
                      aria-label={`Remove redundant override for ${f}`}
                      className="btn btn-link p-0 ml-1 font-weight-bold"
                      style={{
                        fontSize: "0.95rem",
                        lineHeight: 1,
                        color: redundantBadgeStyle.color,
                        verticalAlign: "baseline",
                      }}
                      onClick={() => setBooleanFeatureOverride(f, false)}
                    >
                      ×
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-light" onClick={props.onClose}>
          Close
        </button>
        <button
          disabled={licenseValidationError.length > 0}
          className="btn btn-primary"
          onClick={() => {
            if (
              props.subscription.licenses &&
              licenses < props.subscription.licenses
            ) {
              const confirmed = window.confirm(
                `Are you sure you want to decrease Number of Licenses from ${props.subscription.licenses} to ${licenses}?`,
              );
              if (!confirmed) return;
            }
            if (
              featureOverrides.OrgMonthlyResponseLimitEnabled &&
              !featureOverrides.OrgMonthlyResponseLimit
            ) {
              window.alert(
                `"OrgMonthlyResponseLimit" must be greater than 0 if "OrgMonthlyResponseLimitEnabled" is checked`,
              );
              return;
            }
            props.onSubmit({
              endDate: endDate?.toString(),
              tier,
              clearEndDate,
              isTrial,
              stripeCustomerId,
              stripeSubscriptionItemId,
              billingAdminIds,
              featureOverrides,
              licenses,
              subscribingUserId:
                billingAdminIds?.length > 0 ? billingAdminIds[0] : "",
            });
          }}
        >
          Submit
        </button>
      </ModalFooter>
    </Modal>
  );
};

type BooleanInputProps = {
  id: string;
  defaultValue: boolean;
  onChange: (v: boolean) => void;
};
export const BooleanInput: React.FunctionComponent<BooleanInputProps> = (
  props: BooleanInputProps,
) => (
  <input
    type="checkbox"
    className="form-check-input float-right"
    id={props.id}
    defaultChecked={props.defaultValue}
    onChange={(e) => props.onChange(e.currentTarget.checked)}
  />
);

type NumericInputProps = {
  defaultValue: number;
  minValue: number;
  onChange: (v: number) => void;
};

export const NumericInput: React.FunctionComponent<NumericInputProps> = (
  props: NumericInputProps,
) => (
  <input
    type="number"
    min={props.minValue}
    max={9999999}
    className="form-control"
    value={props.defaultValue ?? ""}
    onChange={(e) => {
      let value = parseInt(e.currentTarget.value, 10);
      if (value === props.minValue) {
        value = NaN;
      }
      props.onChange(value);
    }}
  />
);
